import {
  Media,
  MediaQuestionConfig,
  MediaResponseState,
  MediaType,
  useFindMedia,
  useGrippContext,
} from '@gripp/shared-logic';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Text } from 'react-native-paper';
import { tokens } from '../../../../crop-circle/tokens';
import { isIdpSourceQuestion } from '../../admin/idpQuestions';
import { useFormContext } from '../../../../forms';
import { HorizontalMediaGallery } from '../../../../media';
import {
  RoutineQuestionExecutionForm,
  RoutineQuestionExecutionFormProps,
} from '../types';
import { themedStyleSheet } from '../../../../../themes/themedStyleSheet';

const i18nPrefix = 'routines.workflow.execution.media';

export const MediaQuestionExecutionForm: RoutineQuestionExecutionForm<
  MediaQuestionConfig,
  MediaResponseState
> = (
  props: RoutineQuestionExecutionFormProps<
    MediaQuestionConfig,
    MediaResponseState
  >
) => {
  const { t } = useTranslation();
  const { mediaPickerComponent: MediaPicker } = useGrippContext();
  const fieldName = `responses.${props.responseIndex}.mediaIds`;
  const { control } = useFormContext();

  // Mirrors mediaQuestionAdminForm's `isLockedIdpSource`: `isIdpSource` survives
  // an IDP off-toggle, so this question is only actually image-locked/required
  // while the routine's IDP toggle is on too (plan finding #13).
  const isLockedIdpSource =
    !!props.idpEnabled && isIdpSourceQuestion(props.config);

  control.register(fieldName, {
    value: [],
    validate: (value: string[]) =>
      !isLockedIdpSource || (value?.length ?? 0) > 0,
  });
  const { append, remove } = useFieldArray({
    control,
    name: fieldName,
  });

  const mediaIds: string[] = useWatch({ control, name: fieldName }) ?? [];

  const existingMedia = useFindMedia({
    filter: { id: { in: mediaIds.length > 0 ? mediaIds : undefined } },
  });

  const onMediaAdded = (mediaItem: Media) => {
    append(mediaItem.id);
  };

  const onMediaDeleted = (mediaId: string) => {
    const index = mediaIds.indexOf(mediaId);
    if (index >= 0) remove(index);
  };

  return (
    <>
      <HorizontalMediaGallery
        media={existingMedia}
        onMediaAdded={onMediaAdded}
        mediaPicker={MediaPicker}
        acceptedMediaTypes={
          isLockedIdpSource
            ? [MediaType.Image]
            : [MediaType.Image, MediaType.Video]
        }
        onMediaDeleted={onMediaDeleted}
        canEditMedia={true}
        // The IDP source is a single-document capture: one image, then the add
        // affordance goes away (deleting it brings it back). Both platform
        // pickers are single-select, so the count can only grow one at a time.
        canAddMedia={!isLockedIdpSource || mediaIds.length === 0}
        onUploadingChanged={props.toggleProcessing}
        workspaceId={props.workspaceAsset.workspace.id}
        pickerMarginTop={0}
      />
      {isLockedIdpSource && mediaIds.length === 0 && (
        <Text style={styles.hint}>{t(`${i18nPrefix}.idpImageRequired`)}</Text>
      )}
    </>
  );
};

const styles = themedStyleSheet((role) => ({
  hint: {
    ...tokens.typography.label.medium,
    color: role.textSecondary,
    marginTop: tokens.spacing.xs,
  },
}));
